/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';
import { AssetUtils, Program } from '@wit/vtv-x-foundation-lib';
import translate from 'services/i18n/translate';
import ParentalControl from 'core/services/core/ParentalControl';

interface Props {
  program: Program;
}

const getProgramInfo = (program: Program) => {
  const isSpecialSeason = AssetUtils.isSpecialSeasonNumber(program?.seasonNumber);

  const seasonNumber = program?.seasonNumber;
  const episodeName = program?.episodeName || '';
  const episodeNameElement = (
    <p id="ProgrammeInfoBarMetadata_episodeName" className="episode-name text-elipsis one-line">{`${episodeName}`}</p>
  );

  if (isSpecialSeason || !seasonNumber) {
    if (!episodeName) {
      return null;
    }
    return (
      <div id="ProgrammeInfoBarMetadata_container" className="program-info-container margin-right-20">
        {episodeNameElement}
      </div>
    );
  }

  const seasonNumberLabel = translate.formatString(translate['asset.seasonFormat'], seasonNumber).toString();
  const episodeNumber = program?.episodeNumber;

  if (!episodeNumber) {
    return (
      <div id="ProgrammeInfoBarMetadata_episode" className="program-info-container margin-right-20">
        <p id="ProgrammeInfoBarMetadata_episodeNumber">{`${seasonNumberLabel}`}</p>&nbsp;
        {episodeNameElement}
      </div>
    );
  }

  const episodeNumberLabel = translate.formatString(translate['asset.episodeFormat'], episodeNumber).toString();

  return (
    <div id="ProgrammeInfoBarMetadata_episode" className="program-info-container margin-right-20">
      <p id="ProgrammeInfoBarMetadata_episodeNumber">{`${seasonNumberLabel}, ${episodeNumberLabel}`}</p>&nbsp;
      {episodeNameElement}
    </div>
  );
};

export default function Metadata({ program }: Props): JSX.Element {
  const isAdultContent = ParentalControl.UI.isAdult(program);

  return (
    <div className="pi-bar-metadata">
      {!isAdultContent ? getProgramInfo(program) : null}

      <div className="program-extra-info">
        {!isAdultContent ? (
          <>
            {program.year ? (
              <p id="ProgrammeInfoBarMetadata_year" className="margin-right-20">
                {program.year}
              </p>
            ) : null}
            {program.country ? (
              <p id="ProgrammeInfoBarMetadata_country" className="margin-right-20">
                {program.country}
              </p>
            ) : null}
            {program.genres?.length ? (
              <p id="ProgrammeInfoBarMetadata_genre" className="margin-right-20 text-elipsis one-line">
                {program.genres[0]}
              </p>
            ) : null}
          </>
        ) : null}

        {program.parentalRating ? (
          <div className="parental-control-container">
            <div className="parental-control">
              <span id="ProgrammeInfoBarMetadata_parental">{program.parentalRating}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
